import {Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { lastValueFrom, Subscription } from 'rxjs';
import { AuthService } from 'src/shared/services/auth.service';
import { FileService } from 'src/shared/services/file.service';
import { RegistrationService } from 'src/shared/services/registration.service';
import { ResponseService } from 'src/shared/services/response-service.service';
import { RulesService } from 'src/shared/services/rules.service';
import { SchoolProgramService } from 'src/shared/services/school-program.service';
import { TranslateService } from 'src/shared/services/translate.service';
import Swal from 'sweetalert2';

interface ActivityStatus {
  label: string;
  tone: 'success' | 'info' | 'warning' | 'danger' | 'neutral' | 'blue' | 'cyan' | 'rose';
  hint?: string;
  action?: 'register' | 'payment';
  /** School program named (in italics) inside the label, for the program limit status. */
  program?: string;
}

// Must match the $program-tones list in the stylesheet
const PROGRAM_TONE_COUNT = 8;

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

interface ProgramOption {
  id: any;
  name: string;
  count: number;
}

@Component({
  selector: 'app-online-registration',
  templateUrl: './online-registration.component.html',
  styleUrl: './online-registration.component.scss'
})
export class OnlineRegistrationComponent implements OnInit {
  @ViewChild('registrationSlotModal', {static: true}) registrationSlotModal: NgbModalRef;
  @ViewChild('participationSlotModal', {static: true}) participationSlotModal: NgbModalRef;
  @ViewChild('ruleModal', {static: true}) ruleModal: NgbModalRef;
  @ViewChild('paymentModal', {static: true}) paymentModal: NgbModalRef;

  students: any[] = [];
  studentIdSelected: any;
  ruleList: any;
  globalRule: any;
  studentParticipatedRegistered: any;
  studentSchoolProgramTotalParticipatedRegisteredMap: any;
  role: any;
  studentDetail: any;
  studentId: any;
  dataPeriod: any;
  total = 0;
  hasData = false;
  dataActivity: any;
  availableActivities: any[] = [];
  studentActivity: any;
  studentActivityMap: any;
  ruleMap: any;
  loading = {
    student: false,
    period: false,
    excul: false,
    modal: false,
    payment: false,
    va: false,
    rule_local: false,
    rule_global: false,
    global_checker: false,
  };
  dataLoaded = false;
  isParent = false;
  paymentForm: FormGroup;
  paymentFile: File | null;
  activityRegistrationId: any;
  registrationPaymentId: any;
  activityRegistrationName: any;
  paymentMethods = [''];
  selectedPaymentMethod: string = '';
  registrationPaymentDetail: any;
  paymentModalRef: any;
  arr_date: string[];
  checkBox: any[] = [];
  currentPage = 1;
  today = new Date();
  columnsToDisplay = ['activity', 'schedule', 'session', 'quota', 'fee', 'status'];

  visibleActivities: any[] = [];
  programOptions: ProgramOption[] = [];
  programFilter: any = null;
  searchTerm = '';

  periodStart: any;
  periodEnd: any;
  apiSubs: Subscription;
  disableAutoRegister = false;
  disableAllCheckbox = true;
  slotForGlobalRule: number = 0;
  totalGlobal: number;
  currentTotalGlobal: number;

  schoolLocationId: any;

  totalActivityRegistered: number;

  constructor(
    private registService: RegistrationService,
    private spService: SchoolProgramService,
    private modalService: NgbModal,
    private fileService: FileService,
    private fb: FormBuilder,
    public auth: AuthService,
    private translate: TranslateService,
    private rpSer: ResponseService,
    private ruleService: RulesService
  ){
    this.translate.load('extracurricular', 'registration');
    this.auth.loadPermissions(true);
    this.role = this.auth.current().roles;
    if (this.auth.permissions['SCHOOL_PROGRAM_REGISTRATION_STUDENT_CREATE']) {
      this.columnsToDisplay.unshift('action');
    }
    if (this.auth.permissions['SCHOOL_PROGRAM_REGISTRATION_PAYMENT_STUDENT_VA']) {
      this.paymentMethods.push('BCA Virtual Account');
      this.selectedPaymentMethod = 'BCA Virtual Account';
    }
    if (this.auth.permissions['SCHOOL_PROGRAM_REGISTRATION_PAYMENT_STUDENT_MANUAL']) {
      this.paymentMethods.push('Bank Transfer');
      this.selectedPaymentMethod = 'Bank Transfer';
    }
    this.paymentForm = this.fb.group({
      payment_method: ['', Validators.required],
      bank_account_holder_name: [''],
      bank_account_number: [''],
      payment_amount: [null],
      payment_date: [null],
      payment_notes: [''],
      payment_slip: [null],
      bank_name: [''],
    });
    this.setPaymentMethodValidators('');
  }

  get isBankTransfer(): boolean {
    return this.paymentForm?.get('payment_method')?.value === 'Bank Transfer';
  }

  get isVirtualAccount(): boolean {
    return this.paymentForm?.get('payment_method')?.value === 'BCA Virtual Account';
  }

  get isLatestPaymentApproved(): boolean {
    const logs = this.registrationPaymentDetail?.payment_manual_logs;
    if (!logs || logs.length === 0) return false;
    return logs[0].status === 'approved';
  }

  isGenderMismatch(activity: any): boolean {
    const registration = this.studentActivityMap?.[activity.school_program_activity_id];
    return !!activity.gender_mismatch && !registration?.activity_participant_id && !registration?.activity_registration_id;
  }

  getProgramMax(programId: any): number | null {
    const max = this.ruleMap?.[programId]?.maximum_selection;
    return max === null || max === undefined ? null : +max;
  }

  isProgramLimitReached(programId: any): boolean {
    const max = this.getProgramMax(programId);
    const total = +this.studentSchoolProgramTotalParticipatedRegisteredMap?.[programId]?.total || 0;
    return max !== null && total >= max;
  }

  isGlobalLimitReached(total: number): boolean {
    const max = this.globalRule?.maximum_selection;
    return max !== null && max !== undefined && +total >= +max;
  }

  // slot already accounts for checked-but-not-submitted activities
  private hasProgramSlot(programId: any): boolean {
    const slot = this.studentSchoolProgramTotalParticipatedRegisteredMap?.[programId]?.slot;
    return slot === null || slot === undefined || slot > 0;
  }

  get canCreateRegistration(): boolean {
    return !!this.auth.permissions['SCHOOL_PROGRAM_REGISTRATION_STUDENT_CREATE'];
  }

  get selectedStudentName(): string {
    return this.students.find((student: any) => student.student_id === this.studentId)?.student_name || '';
  }

  get isOutsidePeriod(): boolean {
    return this.today < this.periodStart || this.today > this.periodEnd;
  }

  get isPeriodUpcoming(): boolean {
    return !!this.periodStart && this.today < this.periodStart;
  }

  get isPeriodClosed(): boolean {
    return !!this.periodEnd && this.today > this.periodEnd;
  }

  get isActivityLoading(): boolean {
    return this.loading.excul || this.loading.rule_global || this.loading.rule_local;
  }

  get participatedActivities(): any[] {
    return (this.studentActivity || []).filter((activity: any) => activity.activity_participant_id);
  }

  get pendingActivities(): any[] {
    return (this.studentActivity || []).filter((activity: any) =>
      activity.activity_registration_id && activity.approved === null && !activity.activity_participant_id);
  }

  getInitials(name: string): string {
    return (name || '').split(' ').filter(Boolean).slice(0, 2).map((part: string) => part[0]).join('').toUpperCase();
  }

  getTaken(activity: any): number {
    return (+activity.total_participated || 0) + (+activity.total_registered || 0);
  }

  hasQuotaLimit(activity: any): boolean {
    return activity.max_participant !== null && activity.max_participant !== undefined;
  }

  isFull(activity: any): boolean {
    return this.getTaken(activity) >= activity.max_participant;
  }

  isSelected(activity: any): boolean {
    return this.checkBox.includes(activity);
  }

  isSelectionDisabled(activity: any): boolean {
    const registration = this.studentActivityMap?.[activity.school_program_activity_id];
    return !!registration?.activity_participant_id
      || !!registration?.activity_registration_id
      || this.isFull(activity)
      || this.convertDate(activity.max_registration_date) < this.today
      || this.isOutsidePeriod
      || this.isProgramLimitReached(activity.school_program_id)
      || this.isGlobalLimitReached(this.totalGlobal);
  }

  isRowMuted(activity: any): boolean {
    const registration = this.studentActivityMap?.[activity.school_program_activity_id];
    const isRegistered = registration?.activity_participant_id || registration?.activity_registration_id;
    return !isRegistered && (this.isGenderMismatch(activity) || this.isSelectionDisabled(activity));
  }

  // Participated + registered + currently ticked activities for a program
  getProgramUsed(programId: any): number {
    const total = +this.studentSchoolProgramTotalParticipatedRegisteredMap?.[programId]?.total || 0;
    return total + this.checkBox.filter((activity: any) => activity.school_program_id === programId).length;
  }

  get selectedActivityNames(): string {
    return this.checkBox.map((activity: any) => activity.activity_name).join(', ');
  }

  getQuotaState(used: number, min: any, max: any): 'full' | 'short' | 'ok' {
    if (max !== null && max !== undefined && used >= +max) {
      return 'full';
    }
    if (min !== null && min !== undefined && used < +min) {
      return 'short';
    }
    return 'ok';
  }

  getUsagePercent(used: number, max: any): number {
    if (max === null || max === undefined || +max <= 0) {
      return 0;
    }
    return Math.min((used / +max) * 100, 100);
  }

  getActivityStatuses(activity: any): ActivityStatus[] {
    const registration = this.studentActivityMap?.[activity.school_program_activity_id] || {};
    const isParticipant = !!registration.activity_participant_id;
    const isRegistered = !!registration.activity_registration_id;
    const isUnregistered = !isParticipant && !isRegistered;
    const statuses: ActivityStatus[] = [];

    const isGenderMismatch = this.isGenderMismatch(activity);
    if (isGenderMismatch) {
      statuses.push({ label: 'Not eligible - Gender Mismatch', tone: 'rose' });
    }

    const isFull = this.isFull(activity);
    const isDeadlinePassed = this.convertDate(activity.max_registration_date) < this.today;
    const isClosed = this.isOutsidePeriod || isDeadlinePassed;
    const isProgramLimit = this.isProgramLimitReached(activity.school_program_id);
    const isGlobalLimit = this.isGlobalLimitReached(this.totalGlobal);
    const isPendingApproval = isRegistered && registration.approved === null;
    const isPaid = activity.participation_fee > 0;
    const hasPayment = !!registration.registration_payment_id;

    if (isUnregistered && !isGenderMismatch && !isFull && !isClosed && !isProgramLimit && !isGlobalLimit) {
      if (this.isSelected(activity)) {
        statuses.push({ label: 'Selected', tone: 'info' });
      } else if (this.canCreateRegistration && this.checkBox.length === 0 && !this.isGlobalLimitReached(this.currentTotalGlobal)) {
        statuses.push({ label: 'Register', tone: 'success', action: 'register' });
      } else {
        statuses.push({ label: 'Available', tone: 'success' });
      }
    }
    if (isUnregistered && !isFull && this.isPeriodUpcoming) {
      statuses.push({ label: 'Registration not open yet', tone: 'blue' });
    } else if (isUnregistered && !isFull && isClosed) {
      statuses.push({ label: 'Registration closed', tone: 'blue' });
    }
    if (isUnregistered && isProgramLimit) {
      statuses.push({ label: 'Program limit reached', tone: 'warning', program: activity.school_program_name });
    }
    if (isParticipant) {
      statuses.push({ label: 'Registered', tone: 'success' });
    }
    if (isRegistered && !isParticipant && registration.approved === true) {
      statuses.push({ label: 'Approved', tone: 'success' });
    }
    if (isRegistered && !isParticipant && !isPaid && registration.approved !== false && registration.approved !== true) {
      statuses.push({ label: 'Waiting for Approval', tone: 'info' });
    }
    if (isRegistered && registration.approved === false) {
      statuses.push({ label: 'Rejected', tone: 'danger' });
    }
    if (isPendingApproval && isPaid && !hasPayment && !registration.payment_slip) {
      statuses.push({ label: 'Waiting for Payment', tone: 'warning', hint: 'Click to pay', action: 'payment' });
    }
    if (isPendingApproval && isPaid && hasPayment && !registration.payment_slip) {
      statuses.push({ label: 'Waiting for Payment', tone: 'warning', hint: 'Click for details', action: 'payment' });
    }
    if (isPendingApproval && isPaid && hasPayment && registration.status === 'pending') {
      statuses.push({ label: 'Waiting for Payment Approval', tone: 'info', hint: 'Click for payment details', action: 'payment' });
    }
    if (isPendingApproval && isPaid && hasPayment && registration.status === 'rejected') {
      statuses.push({ label: 'Payment Rejected', tone: 'danger', hint: 'Click to submit another payment', action: 'payment' });
    }
    if (isUnregistered && isFull) {
      statuses.push({ label: 'Full Participant', tone: 'danger' });
    }
    if (isUnregistered && !isProgramLimit && isGlobalLimit) {
      statuses.push({ label: 'Activity limit reached', tone: 'cyan' });
    }
    // Paid registrations whose payment state matched none of the cases above
    if (isRegistered && !isParticipant && registration.approved !== false && !statuses.length) {
      statuses.push({ label: 'Waiting for Approval', tone: 'info' });
    }
    return statuses;
  }

  getRejectReason(activity: any): string | null {
    const registration = this.studentActivityMap?.[activity.school_program_activity_id];
    return registration?.reject_reason && !registration.registration_payment_id ? registration.reject_reason : null;
  }

  onStatusAction(event: any, status: ActivityStatus, activity: any): void {
    if (status.action === 'register') {
      this.onRegisterActivity(event, activity);
    } else if (status.action === 'payment') {
      this.openPaymentModal(activity);
    }
  }

  onSearch(term: string): void {
    this.searchTerm = term;
    this.applyFilters();
  }

  setProgramFilter(programId: any): void {
    this.programFilter = programId;
    this.applyFilters();
  }

  private applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    this.visibleActivities = (this.dataActivity || []).filter((activity: any) =>
      (this.programFilter === null || activity.school_program_id === this.programFilter)
      && (!term || `${activity.activity_name} ${activity.school_program_name}`.toLowerCase().includes(term)));
  }

  // One colour per school program (first seen, first colour), shared by the rule blocks and the table badges
  private programTones = new Map<any, number>();

  getProgramTone(programId: any): string {
    if (!this.programTones.has(programId)) {
      this.programTones.set(programId, this.programTones.size % PROGRAM_TONE_COUNT);
    }
    return 'tone-' + this.programTones.get(programId);
  }

  private buildProgramOptions(): void {
    const options = new Map<any, ProgramOption>();
    (this.dataActivity || []).forEach((activity: any) => {
      const option = options.get(activity.school_program_id)
        || { id: activity.school_program_id, name: activity.school_program_name, count: 0 };
      option.count++;
      options.set(activity.school_program_id, option);
    });
    this.programOptions = [...options.values()];
    if (this.programFilter !== null && !options.has(this.programFilter)) {
      this.programFilter = null;
    }
  }

  clearSelection(): void {
    [...this.checkBox].forEach((activity: any) => this.registerCheckbox({ checked: false }, activity));
  }

  ngOnInit(): void {
    // this.schoolLocationId = this.auth.current().access.school_location[0];
    if (this.role.includes('ORANGTUA') || this.role.includes('PARENT')) {
      this.getChildrenData();
      this.isParent = true;
    } else {
      this.studentId = this.auth.current().user.student_id;
      this.getStudentDetail(this.studentId);
      this.students.push({student_id: this.studentId, student_name: this.auth.current().user.fullname});
      this.isParent = false;
    }
    this.loading.period = true;
    this.loading.excul = true;
    this.dataLoaded = false;
  }
  
  changeStudent(student:any) {
    const studentId = student.student_id;
    // No switching while a bulk register is still sending
    if (studentId === this.studentId || this.disableAutoRegister) {
      return;
    }

    this.studentId = studentId;
    this.resetStudentView();
    this.getStudentDetail(studentId);
  }

  // onShowRegistered() {
  //   this.modalService.open(this.registrationSlotModal, {
  //     size: 'md',
  //   });
  // }

  onShowParticipated() {
    this.modalService.open(this.participationSlotModal, {
      size: 'md',
      centered: true,
    });
  }

  onShowRule() {
    this.modalService.open(this.ruleModal, {
      size: 'xl',
    });
  }

  getChildrenData(studentId: any = this.studentId) {
    this.resetStudentView();
    this.students = [];
    this.registService.getChildren().subscribe(
      (response: any) => {
        if (response.error === 0) {
          response.result.data.forEach((element: any) => {
            this.students.push(element);
          });
          const selectedStudent = this.students.find((student: any) => student.student_id === studentId) || this.students[0];
          if (selectedStudent) {
            this.studentId = selectedStudent.student_id;
            this.getStudentDetail(selectedStudent.student_id);
          }
        } else {
          this.rpSer.showAddError('Failed to get children data. Please try again.');
        }
      },
      (error) => {
        this.rpSer.showAddError();
      }
    );
  }

  getStudentDetail(id: number) {
    this.loading.student = true;
    this.registService.studentAcademicData(id).subscribe(
      (response: any) => {
        if (!this.isCurrentStudent(id)) {
          return;
        }
        if (response.status) {
          const studentDetail = response.data;
          this.studentDetail = studentDetail;
          this.schoolLocationId = studentDetail.school_location_id;
          if (studentDetail) {
            this.getGlobalRule(id, studentDetail.school_location_id);
            this.getRuleList(id, studentDetail);
          }
          this.getPeriod(id);
        } else {
          this.rpSer.showAddError('Failed to get student data. Please try again.');
        }
        this.loading.student = false;
      },
      (error) => {
        if (!this.isCurrentStudent(id)) {
          return;
        }
        this.loading.student = false;
        this.rpSer.showAddError();
      }
    );
  }

  getPeriod(studentId: number = this.studentId) {
    this.loading.period = true;
    this.registService.getPeriodV2(studentId)
      .subscribe(
        (response: any) => {
          if (!this.isCurrentStudent(studentId)) {
            return;
          }
          if (response.status) {
            this.dataPeriod = response.data;
            this.periodStart = new Date(this.dataPeriod.start_date);
            this.periodEnd = new Date(this.dataPeriod.end_date);
            if (this.dataPeriod) {
              this.getExcul(studentId);
            }

          } else {
            // this.rpSer.showAddError(response.message);
          }
          this.loading.period = false;
        },
        (error) => {
          if (!this.isCurrentStudent(studentId)) {
            return;
          }
          this.loading.period = false;
          this.rpSer.showAddError();
        });
  }

  getExcul(studentId: number = this.studentId) {
    this.loading.excul = true;
    this.registService.getExculV2(studentId)
      .subscribe(
        (response: any) => {
          if (!this.isCurrentStudent(studentId)) {
            return;
          }
          if (response.status) {
            this.totalActivityRegistered = 0;
            this.availableActivities = response.data.current_available_activity || [];
            this.studentActivity = response.data.student_activity;
            this.studentActivityMap = this.studentActivity.reduce((acc: any, item: any) => {
              acc[item.school_program_activity_id] = {
                approved: item.approved,
                registration_payment_id: item.registration_payment_id,
                activity_name: item.activity_name,
                activity_participant_id: item.activity_participant_id,
                school_program_name: item.school_program_name,
                school_program_activity_id: item.school_program_activity_id,
                activity_registration_id: item.activity_registration_id,
                reject_reason: item.reject_reason,
                payment_slip: item.payment_slip,
                status: item.status,
              };
              return acc;
            }, {} as any);
            this.total = response.data.length;
            this.hasData = this.total > 0;
            this.dataLoaded = true;
            this.studentActivity.forEach((element: any) => {
              if (element.activity_participant_id || (element.activity_registration_id && element.approved === null)) {
                this.totalActivityRegistered++;
              }
            });
            this.availableActivities.forEach((element: any) => {
              if (element.schedule.length > 0) {
                element.schedule.forEach((day: any) => {
                  day.start_time = day.start_time ? new Date(`1970-01-01T${day.start_time}`) : null;
                  day.end_time = day.end_time ? new Date(`1970-01-01T${day.end_time}`) : null;
                });
                element.schedule.sort((a: any, b: any) =>
                  this.getWeekdayIndex(a.schoolday) - this.getWeekdayIndex(b.schoolday)
                  || (a.start_time?.getTime() ?? 0) - (b.start_time?.getTime() ?? 0));
              }
            });
            this.sortActivities();
          } else {
            this.rpSer.showAddError(response.message);
          }
          this.loading.excul = false;
        },
        (error) => {
          if (!this.isCurrentStudent(studentId)) {
            return;
          }
          this.loading.excul = false;
          this.rpSer.showAddError();
        });
  }

  // Unknown day names sort after Sunday
  private getWeekdayIndex(day: string): number {
    const index = WEEKDAYS.indexOf((day || '').trim().toLowerCase());
    return index === -1 ? WEEKDAYS.length : index;
  }

  getPercentage(element: any): number {
    const max = +element.max_participant || 0;
    const taken = (+element.total_participated || 0) + (+element.total_registered || 0);

    if (max === 0) return 100;

    const percentage = (taken / max) * 100;
    return Math.min(percentage, 100);
  }

  getRuleList(studentId: number = this.studentId, studentDetail: any = this.studentDetail) {
    this.loading.rule_local = true; 
    this.ruleService.getStudentRule({
      year_level_id: +studentDetail.year_level_id,
      school_location_id: +studentDetail.school_location_id,
      student_id: +studentId,
      academic_year: +studentDetail.academic_year,
    })
      .subscribe(
        (response: any) => {
          if (!this.isCurrentStudent(studentId)) {
            return;
          }
          if (response.status) {
            this.ruleList = response.data.rules;
            if (this.ruleList.length > 0) {
              this.ruleMap = this.ruleList.reduce((acc: any, item: any) => {
                acc[item.school_program_id] = {
                  maximum_selection: item.maximum_selection,
                  minimum_selection: item.minimum_selection,
                };
                return acc;
              }, {} as any);
            } else {
              this.ruleMap = null;
            }
            this.studentParticipatedRegistered = response.data.student;
            this.totalGlobal = 0;
            this.studentParticipatedRegistered.forEach((data: any) => {
              this.totalGlobal += +data.total_participated + +data.total_registered;
            });
            this.currentTotalGlobal = this.totalGlobal;
            this.studentSchoolProgramTotalParticipatedRegisteredMap = this.studentParticipatedRegistered.reduce((acc: any, item: any) => {
              const total = +item.total_participated + +item.total_registered;
              const max = this.getProgramMax(item.school_program_id);
              acc[item.school_program_id] = {
                total_participated: item.total_participated,
                total_registered: item.total_registered,
                school_program_name: item.school_program_name,
                total,
                slot: max !== null ? max - total : null,
              };
              return acc;
            }, {} as any);
            this.sortActivities();
          } else {
            this.rpSer.showAddError('Failed to get rule data. Please try again.');
          }
          this.loading.rule_local = false; 
        },
        (error) => {
          if (!this.isCurrentStudent(studentId)) {
            return;
          }
          this.loading.rule_local = false; 
          this.rpSer.showAddError();
        });
  }

  getGlobalRule(studentId: number = this.studentId, schoolLocationId: number = this.schoolLocationId) {
    this.loading.rule_global = true;
    this.ruleService.getLocationGlobalRules(schoolLocationId)
      .subscribe(
        (response: any) => {
          if (!this.isCurrentStudent(studentId)) {
            return;
          }
          if (response.status) {
            this.globalRule = response.data;
            this.sortActivities();
          } else {
            this.rpSer.showAddError('Failed to get global rule data.');
          }
          this.loading.rule_global = false; 
        },
        (error) => {
          if (!this.isCurrentStudent(studentId)) {
            return;
          }
          this.loading.rule_global = false; 
          this.rpSer.showAddError('Failed to get global rule data.');
        });
  }

  private isCurrentStudent(studentId: number): boolean {
    return studentId === this.studentId;
  }

  private sortActivities(): void {
    this.dataActivity = [...this.availableActivities]
      .sort((a: any, b: any) => this.getRegistrationRank(a) - this.getRegistrationRank(b));
    this.buildProgramOptions();
    this.applyFilters();
  }

  private getRegistrationRank(activity: any): number {
    const registration = this.studentActivityMap?.[activity.school_program_activity_id];
    if (registration?.activity_registration_id && registration.approved === null && !registration.activity_participant_id) {
      return 1;
    }
    if (this.isGenderMismatch(activity)) {
      return 3;
    }
    return this.canRegister(activity) ? 0 : 2;
  }

  private canRegister(activity: any): boolean {
    const registration = this.studentActivityMap?.[activity.school_program_activity_id];
    return !(this.today < this.periodStart || this.today > this.periodEnd)
      && !registration?.activity_participant_id
      && !registration?.activity_registration_id
      && (+activity.total_participated + +activity.total_registered) < activity.max_participant
      && this.convertDate(activity.max_registration_date) >= this.today
      && !this.isProgramLimitReached(activity.school_program_id)
      && !this.isGlobalLimitReached(this.totalGlobal);
  }

  private resetStudentView(): void {
    this.checkBox = [];
    this.dataActivity = [];
    this.visibleActivities = [];
    this.programOptions = [];
    this.programFilter = null;
    this.searchTerm = '';
    this.availableActivities = [];
    this.studentActivity = [];
    this.studentActivityMap = {};
    this.studentDetail = null;
    this.dataPeriod = null;
    this.ruleList = [];
    this.ruleMap = null;
    this.globalRule = null;
    this.studentParticipatedRegistered = [];
    this.studentSchoolProgramTotalParticipatedRegisteredMap = {};
    this.periodStart = null;
    this.periodEnd = null;
    this.dataLoaded = false;
    this.loading.student = true;
    this.loading.period = true;
    this.loading.excul = true;
    this.loading.rule_local = true;
    this.loading.rule_global = true;
  }

  convertDate(date: any) {
    return new Date(date);
  }

  checkAvailability(enddate: any) {
    const today = new Date(new Date().setDate(new Date().getDate() - 1));
    const maxRegistrationDate = new Date(enddate);

    if (today > maxRegistrationDate) {
      return false;
    } else {
      return true;
    }
  }

  onRegisterActivity(event: any, data: any) {

    if (this.apiSubs) {
      this.apiSubs.unsubscribe();
    }

    const today = new Date();

    if (today < this.periodStart || today > this.periodEnd) {
      Swal.fire({
        title: 'Error',
        text: 'Registration period is closed',
        icon: 'error'
      });
      return;
    }

    if (!this.hasProgramSlot(data.school_program_id)) {
      Swal.fire({
        title: 'Limit Reached',
        text: `You are only allowed to register ${this.getProgramMax(data.school_program_id)} of ${data.school_program_name}'s activities`,
        icon: 'info'
      });
      event.source.checked = false;
      return;
    }
    if (this.isGlobalLimitReached(this.currentTotalGlobal)) {
      Swal.fire({
        title: 'Limit Reached',
        text: `You are only allowed to register ${this.globalRule.maximum_selection} activities`,
        icon: 'info'
      });
      event.source.checked = false;
      return;
    }

    Swal.fire({
      title: 'Confirmation',
      text: 'Register activity '+data.activity_name+' ?',
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: 'Yes'
    }).then((result) => {
      if (result.isConfirmed) {
          if (event) {
            Swal.fire({
              allowOutsideClick: false,
              showConfirmButton: false,
              showCancelButton: false,
            });
            Swal.showLoading();
            const storeData = {
              menu: 'online_registration',
              'school_program_activity_id': data.school_program_activity_id,
              'student': this.studentDetail,
              'academic': this.dataPeriod,
              'school_location_id': this.schoolLocationId,
              'activity_name': data.activity_name,
            };
            this.registService.registActivity(storeData)
            .subscribe(async (response: any) => {
              if (response.status) {
                if (response.message === 'Successfully Registered') {
                  this.rpSer.showAddSuccess('Registration Success');
                } else {
                  if (response.message ? response.message.split(" ")[0] === 'Successfully' : false) {
                    this.rpSer.showAddSuccess(response.message ? response.message : 'Registration Success');
                  } else {
                    this.rpSer.showAddError(response.message ? response.message : 'Registration Failed');
                  }
                }

                this.reloadAfterRegister();
              } else {
                this.rpSer.showAddError('Registration Failed');
                this.reloadAfterRegister();
              }
            }, (error: any) => {
              if (error.message) {
                // Swal.fire({
                //   title: 'Error',
                //   text: error.message,
                //   icon: 'error'
                // }).then(() => {
                //   this.getExcul();
                // });
                this.rpSer.showAddError(error.message ? error.message : 'An unexpected error occurred. Please check your connection and try again.');
              } else {
                this.rpSer.showAddError('Registration Failed');
                this.getExcul();
              }
            });
          }
      }
    });
    this.getRuleList();
  }

  async autoRegister(data: any, context: { student: any; academic: any; schoolLocationId: any }) {

    if (this.apiSubs) {
      this.apiSubs.unsubscribe();
    }

    const storeData = {
      menu: 'online_registration',
      'school_program_activity_id': data.school_program_activity_id,
      'student': context.student,
      'academic': context.academic,
      'school_location_id': context.schoolLocationId,
      'activity_name': data.activity_name,
    };
    try {
      const response = await lastValueFrom(this.registService.registActivity(storeData));
      if (response.status) {
        if (response.message.split(" ")[0] === 'Successfully') {
          await this.rpSer.showAddSuccess(response.message ? response.message : `Registration of activity ${data.activity_name} Success`);
        } else {
          await this.rpSer.showAddError(response.message ? response.message : `Registration of activity ${data.activity_name} failed`);
        }
      } else {
        await this.rpSer.showAddError(response.message ? response.message : `Registration of activity ${data.activity_name} failed`);
      }
    } catch (error: any) {
      await this.rpSer.showAddError(error.message ? error.message : `Unknown error (activity ${data.activity_name})`);
    }
  }

  registerCheckbox(event: any, activity: any) {
    if (event.checked) {
      const today = new Date();
      if (today < this.periodStart || today > this.periodEnd) {
        Swal.fire({
          title: 'Error',
          text: 'Registration period is closed',
          icon: 'error'
        });
        event.source.checked = false;
        return;
      }
      if (!this.hasProgramSlot(activity.school_program_id)) {
        Swal.fire({
          title: 'Limit Reached',
          text: `You are only allowed to register ${this.getProgramMax(activity.school_program_id)} of ${activity.school_program_name}'s activities`,
          icon: 'info'
        });
        event.source.checked = false;
        return;
      }
      if (this.isGlobalLimitReached(this.currentTotalGlobal)) {
        Swal.fire({
          title: 'Limit Reached',
          text: `You are only allowed to register ${this.globalRule.maximum_selection} activities`,
          icon: 'info'
        });
        event.source.checked = false;
        return;
      }
      this.currentTotalGlobal = this.currentTotalGlobal + 1;
      const programQuota = this.studentSchoolProgramTotalParticipatedRegisteredMap?.[activity.school_program_id];
      if (programQuota && programQuota.slot !== null) {
        programQuota.slot = programQuota.slot - 1;
      }
      this.checkBox.push(activity);
    } else {
      this.currentTotalGlobal = this.currentTotalGlobal - 1;
      const programQuota = this.studentSchoolProgramTotalParticipatedRegisteredMap?.[activity.school_program_id];
      if (programQuota && programQuota.slot !== null) {
        programQuota.slot = programQuota.slot + 1;
      }
      const index = this.checkBox.indexOf(activity);
      if (index !== -1) {
          this.checkBox.splice(index, 1);
      }
    }
  }

  async onBulkRegist() {
    if (this.disableAutoRegister) {
      return;
    }
    this.disableAutoRegister = true;

    if (this.apiSubs) {
      this.apiSubs.unsubscribe();
    }

    // Snapshot the student and selection up front, so nothing that changes mid-loop
    // (child switch, ticking more boxes) can send these activities for someone else
    const context = {
      student: this.studentDetail,
      academic: this.dataPeriod,
      schoolLocationId: this.schoolLocationId,
    };
    const periodStart = this.periodStart;
    const periodEnd = this.periodEnd;
    const activities = [...this.checkBox];

    for (const activity of activities) {
      const today = new Date();

      if (today < periodStart || today > periodEnd) {
        Swal.fire({
          title: 'Error',
          text: 'Registration period is closed',
          icon: 'error'
        });
        continue;
      }

      await this.autoRegister(activity, context);
    }
    this.disableAutoRegister = false;
    this.reloadAfterRegister();
  }

  private reloadAfterRegister(): void {
    this.checkBox = [];
    this.getRuleList();
    this.getExcul();
    if (this.isParent) {
      this.getChildrenData();
    } else {
      this.getStudentDetail(this.studentId);
    }
  }
  

  onChangePaymentMethod(method: string) {
    this.setPaymentMethodValidators(method);
  }

  closePaymentSection() {
    if (this.paymentModalRef) {
      this.paymentModalRef.dismiss();
    }
    this.resetPaymentForm();
  }

  private setPaymentMethodValidators(method: string) {
    const isBankTransfer = method === 'Bank Transfer';
    const controls = ['bank_account_holder_name', 'bank_account_number', 'payment_amount', 'payment_date', 'bank_name'];

    controls.forEach((controlName: string) => {
      const control = this.paymentForm.get(controlName);
      if (!control) {
        return;
      }

      if (isBankTransfer) {
        control.setValidators([Validators.required]);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  private resetPaymentForm() {
    this.activityRegistrationId = null;
    this.registrationPaymentId = null;
    this.activityRegistrationName = null;
    this.registrationPaymentDetail = null;
    this.paymentFile = null;
    this.loading.payment = false;
    this.loading.va = false;
    this.paymentForm.reset({
      payment_method: '',
      bank_account_holder_name: '',
      bank_account_number: '',
      payment_amount: null,
      payment_date: null,
      payment_notes: null,
      bank_name: null,
    });
    this.setPaymentMethodValidators('');
  }

  openPaymentModal(data: any) {
    this.resetPaymentForm();
    this.activityRegistrationId = this.studentActivityMap[data.school_program_activity_id]?.activity_registration_id;
    this.registrationPaymentId = this.studentActivityMap[data.school_program_activity_id]?.registration_payment_id;
    this.activityRegistrationName = data.activity_name;

    this.paymentModalRef = this.modalService.open(this.paymentModal as any, {
      size: 'xl',
      centered: true,
      backdrop: 'static'
    });

    if (!this.registrationPaymentId) {
      return;
    }

    this.loading.va = true;
    this.registService.getStudentRegistrationPaymentDetailByActivity(this.registrationPaymentId).subscribe({
      next: (response: any) => {
        if (response.status) {
          this.registrationPaymentDetail = response.data;
          let defaultPaymentMethod = '';
          if (this.registrationPaymentDetail.payment_generated) {
            defaultPaymentMethod = 'BCA Virtual Account';
          } else if (this.auth.permissions['SCHOOL_PROGRAM_REGISTRATION_PAYMENT_STUDENT_MANUAL']) {
            defaultPaymentMethod = 'Bank Transfer';
          } else if (this.auth.permissions['SCHOOL_PROGRAM_REGISTRATION_PAYMENT_STUDENT_VA']) {
            defaultPaymentMethod = 'BCA Virtual Account';
          }
          this.paymentForm.patchValue({ payment_method: defaultPaymentMethod });
          this.setPaymentMethodValidators(defaultPaymentMethod);
          this.paymentForm.patchValue({
            bank_account_holder_name: this.registrationPaymentDetail.bank_account_holder_name || null,
            bank_account_number: this.registrationPaymentDetail.bank_account_number || null,
            payment_amount: this.registrationPaymentDetail.total_fee,
            payment_date: this.registrationPaymentDetail.payment_date ? new Date(this.registrationPaymentDetail.payment_date) : null,
            payment_notes: this.registrationPaymentDetail.payment_notes || null,
            payment_slip: this.registrationPaymentDetail.payment_slip || null,
          })
        } else {
          this.registrationPaymentDetail = null;
          this.rpSer.showAddError('Failed to get payment data.');
        }
        this.loading.va = false;
      },
      error: (error: any) => {
        this.rpSer.showAddError('Failed to get payment data.');
        this.loading.va = false;
      }
    })
  }

  onChangeDigitalFile(item: any): void {
    this.paymentFile = null;
    if (item.target.files.length > 0) {
      this.paymentFile = item.target.files[0];
    }
  }

  async onConfirmManualPayment() {
    if (!this.isBankTransfer) {
      return;
    }

    this.loading.payment = true;
    if (this.paymentForm.invalid || !this.paymentFile) {
      Swal.fire({
        title: 'Warning',
        text: 'Please fill all required forms',
        icon: 'error'
      });
      this.loading.payment = false;
      return;

    }

    try {
      const collectionFile = await this.fileService.uploadFileS3(this.paymentFile, 'school_program/payment');
      const fileParams = { name: collectionFile.name, path: collectionFile.path, url: collectionFile.url, size: collectionFile.size };
      if (fileParams.size > 15000000) {
        Swal.fire({
          title: 'Warning',
          text: 'File exceed maximum limit of 15MB',
          icon: 'warning'
        });
        this.loading.payment = false;
        return;
      }

      const formValue = this.paymentForm.value;
      const params = {
        bank_account_holder_name: formValue.bank_account_holder_name,
        bank_account_number: formValue.bank_account_number,
        payment_amount: formValue.payment_amount,
        payment_date: formValue.payment_date,
        payment_slip: fileParams,
        payment_notes: formValue.payment_notes,
        activity_registration_id: this.activityRegistrationId,
        registration_payment_id: this.registrationPaymentId,
        bank_name: formValue.bank_name,
      };
      this.registService.confirmManualPayment(this.studentId, params)
      .subscribe(async (response: any) => {
        if (response.status) {
          this.rpSer.showAddSuccess('Registration Success');
          this.paymentModalRef.close();
          this.resetPaymentForm();
          this.getExcul();
        } else {
          this.rpSer.showAddError('Payment Submission Failed');
        }
        this.loading.payment = false;
      }, (error: any) => {
        this.rpSer.showAddError('Payment Submission Failed');
        this.loading.payment = false;
      });
    } catch (error: any) {
      this.rpSer.showAddError('Payment Submission Failed');
      this.loading.payment = false;
    }
  }

  checkGlobalRule() {
    this.loading.global_checker = true;
    this.slotForGlobalRule = 0;
    if (!this.studentSchoolProgramTotalParticipatedRegisteredMap) {
      return;
    }
    if (!this.globalRule) {
      this.disableAllCheckbox = false;
      return;
    }
    const maxSelectionGlobal = this.globalRule.maximum_selection;
    this.slotForGlobalRule = maxSelectionGlobal - this.totalGlobal;
    if (this.slotForGlobalRule > 0) {
      this.disableAllCheckbox = false;
    } else {
      this.disableAllCheckbox = true;
    }
    this.loading.global_checker = false;
  }

}
